import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import './PrivateLessons.css';
import {
  API_BASE,
  authHeaders,
  buildCalendarDays,
  formatDateTime,
  formatTime,
  getMonthLabel,
  toDateKey
} from './privateLessonUtils.js';

function ReschedulePrivateLesson() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [lesson, setLesson] = useState(null);
  const [slots, setSlots] = useState([]);
  const [duration, setDuration] = useState(60);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const date = new Date();
    date.setDate(1);
    date.setHours(0, 0, 0, 0);
    return date;
  });
  const [selectedDateKey, setSelectedDateKey] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [savingStartAt, setSavingStartAt] = useState('');

  const fetchLesson = async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(`${API_BASE}/private-lesson/get/${id}`, {
        headers: authHeaders()
      });
      const data = res.data?.data || null;
      setLesson(data);
      setDuration(Number(data?.duration_minutes || 60));
    } catch (err) {
      console.error('Error fetching private lesson', err);
      setLesson(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLesson();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchSlots = async () => {
    if (!lesson?.student_id || !lesson?.docent_id) return;

    try {
      setIsLoadingSlots(true);
      const res = await axios.get(`${API_BASE}/private-lesson/booking/slots`, {
        params: {
          student_id: lesson.student_id,
          docent_id: lesson.docent_id,
          subject: lesson.subject || undefined,
          location_id: lesson.location_id || undefined,
          duration_minutes: duration,
          from: toDateKey(currentMonth),
          days: 42
        },
        headers: authHeaders()
      });

      setSlots(Array.isArray(res.data?.data?.slots) ? res.data.data.slots : []);
      setSelectedDateKey('');
    } catch (err) {
      console.error('Error fetching reschedule slots', err);
      setSlots([]);
      alert(err?.response?.data?.error || 'Beschikbare tijden ophalen mislukt.');
    } finally {
      setIsLoadingSlots(false);
    }
  };

  useEffect(() => {
    if (lesson) fetchSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesson, duration, currentMonth]);

  const slotsByDate = useMemo(() => {
    const map = new Map();
    slots.forEach((slot) => {
      const dateKey = toDateKey(slot.start_at);
      if (!dateKey) return;
      const current = map.get(dateKey) || [];
      current.push(slot);
      map.set(dateKey, current.sort((a, b) => new Date(a.start_at) - new Date(b.start_at)));
    });
    return map;
  }, [slots]);

  const selectedDateSlots = useMemo(
    () => (selectedDateKey ? slotsByDate.get(selectedDateKey) || [] : []),
    [selectedDateKey, slotsByDate]
  );

  const calendarDays = useMemo(() => buildCalendarDays(currentMonth), [currentMonth]);

  const handleReschedule = async (slot) => {
    const reason = window.prompt('Waarom wil je deze priveles wijzigen? Je mag dit ook leeg laten.');
    if (reason === null) return;

    try {
      setSavingStartAt(slot.start_at);
      const res = await axios.post(
        `${API_BASE}/private-lesson/${id}/reschedule`,
        {
          start_at: slot.start_at,
          duration_minutes: duration,
          reason: reason || null
        },
        { headers: authHeaders() }
      );

      alert(res.data?.message || 'Wijziging aangevraagd.');
      navigate('/my-private-lessons');
    } catch (err) {
      console.error('Error rescheduling private lesson', err);
      alert(err?.response?.data?.error || 'Wijzigen mislukt.');
    } finally {
      setSavingStartAt('');
    }
  };

  if (isLoading) {
    return <div className="bg-white p-4">Loading...</div>;
  }

  if (!lesson) {
    return (
      <div className="bg-white p-4">
        <div className="alert alert-warning">Priveles niet gevonden.</div>
        <Link to="/my-private-lessons" className="btn btn-secondary w-100 rounded-0">Terug</Link>
      </div>
    );
  }

  return (
    <div className="bg-white p-4">
      <h2 className="text-center">Priveles wijzigen</h2>
      <hr className="seperator" />

      <div className="alert alert-info">
        <strong>{lesson.subject || 'Priveles'}</strong><br />
        Huidig moment: {formatDateTime(lesson.start_at)}<br />
        Docent: {lesson.docent_name || '-'}<br />
        Leerling: {lesson.student_name || '-'}
      </div>

      <div className="row mb-3">
        <div className="col-md-4">
          <label>Duur</label>
          <select className="form-select rounded-0" value={duration} onChange={(e) => setDuration(Number(e.target.value))}>
            <option value={60}>60 minuten</option>
            <option value={90}>90 minuten</option>
            <option value={120}>120 minuten</option>
          </select>
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}>Vorige</button>
        <h4 className="mb-0 text-capitalize">{getMonthLabel(currentMonth)}</h4>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}>Volgende</button>
      </div>

      {isLoadingSlots ? (
        <p>Beschikbare momenten laden...</p>
      ) : slots.length === 0 ? (
        <div className="alert alert-warning">Geen beschikbare momenten gevonden.</div>
      ) : (
        <>
          <div className="table-responsive mb-4">
            <table className="table table-bordered text-center align-middle private-calendar-table">
              <thead><tr><th>Ma</th><th>Di</th><th>Wo</th><th>Do</th><th>Vr</th><th>Za</th><th>Zo</th></tr></thead>
              <tbody>
                {Array.from({ length: Math.ceil(calendarDays.length / 7) }).map((_, weekIndex) => (
                  <tr key={weekIndex}>
                    {calendarDays.slice(weekIndex * 7, weekIndex * 7 + 7).map((day, dayIndex) => {
                      if (!day) return <td key={`empty-${dayIndex}`} />;
                      const dateKey = toDateKey(day);
                      const hasSlots = slotsByDate.has(dateKey);
                      return (
                        <td key={dateKey} style={{ height: 72 }}>
                          <button
                            type="button"
                            className={`btn btn-sm w-100 ${hasSlots ? 'btn-success' : 'btn-outline-light'} ${selectedDateKey === dateKey ? 'border border-dark' : ''}`}
                            disabled={!hasSlots}
                            onClick={() => setSelectedDateKey(dateKey)}
                          >
                            <div>{day.getDate()}</div>
                            {hasSlots && <small>{slotsByDate.get(dateKey).length} moment(en)</small>}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!selectedDateKey ? (
            <div className="alert alert-light border">Klik op een groene dag om tijden te bekijken.</div>
          ) : (
            <div className="private-slot-list">
              {selectedDateSlots.map((slot) => (
                <div key={slot.start_at} className="private-slot-card">
                  <div>
                    <div className="private-slot-title">{formatTime(slot.start_at)} - {formatTime(slot.end_at)}</div>
                    <div className="private-slot-subtitle">{formatDateTime(slot.start_at)}</div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    disabled={savingStartAt === slot.start_at}
                    onClick={() => handleReschedule(slot)}
                  >
                    {savingStartAt === slot.start_at ? 'Bezig...' : 'Wijzig naar dit moment'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <div className="mt-4">
        <Link to="/my-private-lessons" className="btn btn-secondary w-100 rounded-0">Terug</Link>
      </div>
    </div>
  );
}

export default ReschedulePrivateLesson;
